import type { FC, ReactNode } from "react"
import React, { useState } from "react"
import type { GetServerSideProps } from "next"
import {
  Text,
  Flex,
  Stack,
  Button,
  Icon,
  createIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
} from "@chakra-ui/react"
import { ChevronDownIcon } from "@chakra-ui/icons"

import type { Chain } from "query/graphql"
import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import { TALLY_DEFAULT_SOCIAL_PREVIEW } from "common/constants/logo"
import { fetchChains } from "common/helpers/fetch"
import { getMainnetChainIds } from "governance/helpers/governance"
import { useDevice } from "common/hooks/useDevice"
import Criteria from "common/components/Criteria"
import { isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import type { ChainId } from "web3/constants/chains"
import chains, { MainnetFilters } from "web3/constants/chains"
import { extractViewBox } from "common/helpers/utils"
import { getSeconds } from "common/helpers/seconds"
import OrganizationTable from "proposal/components/OrganizationTable"
import ErrorBoundary from "common/components/ErrorBoundary"
import { spacing } from "theme/foundations/spacing"

const getDaoFilters = (): Record<string, Array<ChainId>> => {
  const allMainnetChainIds: ChainId[] = getMainnetChainIds()

  return allMainnetChainIds.reduce(
    (daoFilters, chainId) => {
      const chain = chains[chainId]
      const { isTestnet, mediumName } = chain

      if (!isTestnet) {
        return {
          ...daoFilters,
          [mediumName]: [chainId],
        }
      }

      return daoFilters
    },
    {
      All: allMainnetChainIds,
    },
  )
}

function Explore({ chains }: { chains: Chain[] }) {
  const [currentFilter, setCurrentFilter] = useState<string>(
    MainnetFilters[MainnetFilters.All],
  )
  const [criteria, setCriteria] = useState<string>("")
  const { onLargeDevice, onLittleDevice } = useDevice()

  const preview = {
    title: "Tally | Explore DAOs",
    image: TALLY_DEFAULT_SOCIAL_PREVIEW,
    description:
      "Tally is a DAO operations platform. DAOs use Tally to create and pass proposals, enable delegation, and power voting.",
  }

  const chain = chains.find((chain) => chain.mediumName === currentFilter)

  return (
    <MainLayout>
      <MainLayoutHead preview={preview} title="Tally - Explore DAOs" />
      <MainLayoutHeader />
      <MainLayoutContainer>
        <Flex direction="column" gap={spacing.containerGap}>
          <ErrorBoundary section="ExploreDaosHeader">
            <Stack display={onLittleDevice} spacing={spacing.containerGap}>
              <Title>Explore DAOs</Title>
              <Stack
                direction="column"
                spacing={spacing.containerGap}
                width="100%"
              >
                <Filters
                  currentFilter={currentFilter}
                  setCurrentFilter={setCurrentFilter}
                />
                <Criteria
                  bg="white"
                  criteria={criteria}
                  setCriteria={setCriteria}
                  w={{ base: "100%", sm: "180px" }}
                />
              </Stack>
            </Stack>
            <Flex
              bg="white"
              border="1px solid hsla(220, 17%, 93%, 1)"
              direction="column"
              display={onLargeDevice}
              rounded="10px"
              w="full"
            >
              <Flex
                align="center"
                justify="space-between"
                px={4}
                py={6}
                w="full"
              >
                <Title>Explore DAOs</Title>
                <HStack align="right">
                  <Filters
                    currentFilter={currentFilter}
                    setCurrentFilter={setCurrentFilter}
                  />
                  <Criteria
                    criteria={criteria}
                    setCriteria={setCriteria}
                    w="sm"
                  />
                </HStack>
              </Flex>
            </Flex>
          </ErrorBoundary>
          <ErrorBoundary section="OrganizationTable">
            <OrganizationTable chainId={chain?.id} searchString={criteria} />
          </ErrorBoundary>
        </Flex>
      </MainLayoutContainer>
      <MainLayoutFooter mt={{ base: 20, lg: 20 }} />
    </MainLayout>
  )
}

function Title({ children }: { children: ReactNode }) {
  return (
    <Text
      color="neutral.700"
      fontSize=" 20px"
      fontWeight="bold"
      lineHeight="28px"
    >
      {children}
    </Text>
  )
}

const Filters: FC<{
  currentFilter: string
  setCurrentFilter: React.Dispatch<React.SetStateAction<string>>
}> = ({ currentFilter, setCurrentFilter }) => {
  const daoFilters = getDaoFilters()

  const getNetworkIcon = (filter: string) => {
    const getIcon = (filter: string) => {
      if (filter === MainnetFilters[MainnetFilters.All]) return undefined
      else {
        const foundChain = Object.values(chains).find(
          (chain) => chain.mediumName === filter,
        )

        return foundChain
      }
    }

    const chain = getIcon(filter as string)

    const networkIcon =
      chain && chain.svg
        ? createIcon({
            displayName: chain.mediumName,
            viewBox: extractViewBox(chain.svg),

            path: <g dangerouslySetInnerHTML={{ __html: chain.svg }} />,
          })
        : undefined

    return networkIcon
  }

  const currentNetworkIcon = getNetworkIcon(currentFilter)

  return (
    <Menu isLazy>
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            bgColor="white"
            fontSize="md"
            fontWeight="regular"
            leftIcon={
              currentFilter !== MainnetFilters[MainnetFilters.All] ? (
                <Icon as={currentNetworkIcon} />
              ) : undefined
            }
            px={4}
            rightIcon={<ChevronDownIcon />}
            textAlign="left"
            variant="secondary"
            width="100%"
          >
            {currentFilter === MainnetFilters[MainnetFilters.All]
              ? `${currentFilter} networks`
              : currentFilter}
          </MenuButton>
          {isOpen ? (
            <MenuList
              boxShadow="lg"
              maxHeight="350px"
              maxW="100vw"
              overflowY="auto"
              width={{ base: "90vw", sm: "100%" }}
            >
              {Object.keys(daoFilters).map((filter) => {
                const isSelected = Boolean(currentFilter === filter)

                const networkIcon = getNetworkIcon(filter)

                return (
                  <MenuItem
                    key={`chain-${filter}`}
                    isDisabled={isSelected}
                    minH="48px"
                    onClick={() => setCurrentFilter(filter)}
                  >
                    <HStack align="center">
                      {networkIcon ? <Icon as={networkIcon} mr={2} /> : null}

                      <>{filter}</>
                    </HStack>
                  </MenuItem>
                )
              })}
            </MenuList>
          ) : null}
        </>
      )}
    </Menu>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { res } = context
  res.setHeader(
    "Cache-Control",
    `s-maxage=${getSeconds({ hours: 3 })}, stale-while-revalidate=${getSeconds({
      hours: 6,
    })}`,
  )

  if (isWhitelabel(context)) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const chains = await fetchChains()

  return {
    props: {
      chains,
    },
  }
}

export default Explore
